// src/components/verification/DiditVerification.tsx
// Composant de vérification d'identité Didit
// Popup sur desktop (optimisation UX), redirection complète sur mobile
// (comportement natif fiable — voir verification-complete.tsx pour la reprise)

import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { db } from '@/lib/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { Shield, CheckCircle, XCircle, Clock, ExternalLink, Loader2 } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────
export type VerificationRole = 'lender' | 'consultant' | 'partner';
export type EntityType = 'individual' | 'business';
type VerificationStatus = 'idle' | 'pending' | 'approved' | 'declined' | 'review';

interface DiditVerificationProps {
  role: VerificationRole;
  entityType: EntityType;
  onApproved: () => void;
  onDeclined?: () => void;
}

// ─── IDs workflows Didit (production) ────────────────────────────────────────
const WORKFLOWS = {
  KYC_FREE: '6b401ea4-39ac-4af4-8282-dfd022edafd9',
  KYC_AML:  '15cfddcc-97e4-4bee-a89d-1c816d9d4db2',
  KYB:      '4d0fe53f-c56d-4136-85b8-7047544c178b',
} as const;

// ─── Textes bilingues ─────────────────────────────────────────────────────────
const T = {
  en: {
    title: 'Identity Verification',
    subtitle: 'Required before activating your account',
    description: {
      lender_individual:    'As a lender, we are required to verify your identity and conduct an AML screening to comply with financial regulations.',
      lender_business:      'As a financial institution, we need to verify your company and conduct AML compliance checks.',
      consultant_individual:'We need to verify your identity before activating your consultant profile.',
      consultant_business:  'We need to verify your company before activating your consultant profile.',
      partner_individual:   'We need to verify your identity and professional credentials before activating your Expert profile.',
      partner_business:     'We need to verify your company before activating your Expert profile.',
    },
    startBtn:    'Start Verification',
    continueBtn: 'Continue Verification',
    waitingTitle: 'Verification in Progress',
    waitingText:  'Please complete your verification in the Didit window. This page will update automatically once done.',
    approvedTitle: 'Verification Approved ✓',
    approvedText:  'Your identity has been successfully verified. You may now proceed.',
    declinedTitle: 'Verification Declined',
    declinedText:  'Unfortunately your verification was declined. Please contact support for assistance.',
    reviewTitle:   'Under Review',
    reviewText:    'Your verification is being reviewed by our team. You will receive an email notification shortly.',
    continueText:  'Continue',
    contactSupport: 'Contact Support',
    secure: 'Secure verification powered by Didit',
  },
  fr: {
    title: 'Vérification d\'identité',
    subtitle: 'Requise avant l\'activation de votre compte',
    description: {
      lender_individual:    'En tant que prêteur, nous devons vérifier votre identité et effectuer une vérification AML conformément aux réglementations financières.',
      lender_business:      'En tant qu\'institution financière, nous devons vérifier votre entreprise et effectuer les vérifications de conformité AML.',
      consultant_individual:'Nous devons vérifier votre identité avant d\'activer votre profil consultant.',
      consultant_business:  'Nous devons vérifier votre entreprise avant d\'activer votre profil consultant.',
      partner_individual:   'Nous devons vérifier votre identité et vos accréditations professionnelles avant d\'activer votre profil Expert.',
      partner_business:     'Nous devons vérifier votre entreprise avant d\'activer votre profil Expert.',
    },
    startBtn:    'Démarrer la vérification',
    continueBtn: 'Continuer la vérification',
    waitingTitle: 'Vérification en cours',
    waitingText:  'Veuillez compléter votre vérification dans la fenêtre Didit. Cette page se mettra à jour automatiquement.',
    approvedTitle: 'Vérification approuvée ✓',
    approvedText:  'Votre identité a été vérifiée avec succès. Vous pouvez maintenant continuer.',
    declinedTitle: 'Vérification refusée',
    declinedText:  'Malheureusement, votre vérification a été refusée. Veuillez contacter le support.',
    reviewTitle:   'En cours de révision',
    reviewText:    'Votre vérification est examinée par notre équipe. Vous recevrez une notification par courriel sous peu.',
    continueText:  'Continuer',
    contactSupport: 'Contacter le support',
    secure: 'Vérification sécurisée propulsée par Didit',
  },
};

// ─── Sélection du workflow selon rôle + type ──────────────────────────────────
function selectWorkflow(role, entityType) {
  if (entityType === 'business') return WORKFLOWS.KYB;
  if (role === 'lender')        return WORKFLOWS.KYC_AML;
  return WORKFLOWS.KYC_FREE;
}

// ─── Construction de la vendor_data ──────────────────────────────────────────
function buildVendorData(uid, role, entityType) {
  return `uid:${uid}|role:${role}|type:${entityType}`;
}

// ─── Détection mobile (heuristique — pas de méthode 100% fiable) ─────────────
function isLikelyMobile() {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(navigator.userAgent);
}

// ─── Persistance du contexte de reprise ────────────────────────────────────
const RESUME_KEY = 'didit_resume';

function saveResumeContext(role, uid, sessionId) {
  try {
    sessionStorage.setItem(
      RESUME_KEY,
      JSON.stringify({ role, uid, sessionId: sessionId || null, createdAt: Date.now() })
    );
  } catch {
    /* sessionStorage indisponible — verification-complete.tsx retombera
       sur son mode "no-context". */
  }
}

function clearResumeContext() {
  try {
    sessionStorage.removeItem(RESUME_KEY);
  } catch {
    /* ignore */
  }
}

// ─── Composant principal ──────────────────────────────────────────────────────
export function DiditVerification({
  role,
  entityType,
  onApproved,
  onDeclined,
}) {
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = T[language === 'fr' ? 'fr' : 'en'];

  const [status, setStatus]   = useState('idle');
  const [started, setStarted] = useState(false);
  const popupRef = useRef(null);

  const workflowId  = selectWorkflow(role, entityType);
  const descKey     = `${role}_${entityType}`;
  const description = t.description[descKey] || t.description[`${role}_individual`];

  useEffect(() => {
    if (!user?.uid || !started) return;

    const unsubscribe = onSnapshot(
      doc(db, 'users', user.uid),
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        const bgStatus = data?.backgroundCheckStatus;
        const diditStatus = data?.diditVerification?.status;

        if (bgStatus === 'approved' || diditStatus === 'Approved') {
          clearResumeContext();
          popupRef.current?.close();
          setStatus('approved');
          setTimeout(() => onApproved(), 1500);
        } else if (bgStatus === 'rejected' || diditStatus === 'Declined') {
          clearResumeContext();
          popupRef.current?.close();
          setStatus('declined');
          onDeclined?.();
        } else if (diditStatus === 'In Review') {
          popupRef.current?.close();
          setStatus('review');
        }
      },
      (error) => {
        console.error('[DiditVerification] Firestore listener error:', error.code, error.message);
      }
    );

    return () => unsubscribe();
  }, [user?.uid, started, onApproved, onDeclined]);

  const handleStart = useCallback(async () => {
    if (!user?.uid) return;

    setStatus('pending');
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const fn = httpsCallable(getFunctions(), 'createDiditSession');
      const vendorData = buildVendorData(user.uid, role, entityType);
      const result = await fn({
        workflowId,
        vendorData,
       callback: `${window.location.origin}/verification-complete`,
      });
      const { url, sessionId } = result.data;

      saveResumeContext(role, user.uid, sessionId);

      if (isLikelyMobile()) {
        window.location.href = url;
        return;
      }

      popupRef.current = window.open(url, 'didit-verification', 'width=500,height=700');
      if (!popupRef.current) {
        window.location.href = url;
        return;
      }
      setStarted(true);
    } catch (err) {
      console.error('[DiditVerification] Failed to create session:', err);
      setStatus('idle');
    }
  }, [user?.uid, workflowId, role, entityType]);

  return (
    <div className="flex flex-col items-center gap-6 p-6 max-w-lg mx-auto">
      <div className="text-center">
        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-[#1D314D]/10 mx-auto mb-4">
          <Shield className="w-8 h-8 text-[#1D314D]" />
        </div>
        <h2 className="text-2xl font-bold text-[#1D314D]">{t.title}</h2>
        <p className="text-sm text-gray-500 mt-1">{t.subtitle}</p>
      </div>

      {status === 'idle' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800 text-center">
          {description}
        </div>
      )}

      {status === 'idle' && (
        <button
          onClick={handleStart}
          className="flex items-center gap-2 px-6 py-3 bg-[#1D314D] text-white rounded-lg hover:bg-[#1D314D]/90 transition font-medium"
        >
          <ExternalLink className="w-4 h-4" />
          {t.startBtn}
        </button>
      )}

      {status === 'pending' && (
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center gap-3 text-[#1D314D]">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="font-semibold">{t.waitingTitle}</span>
          </div>
          <p className="text-sm text-gray-500">{t.waitingText}</p>
          <button
            onClick={handleStart}
            className="text-sm text-[#1D314D] underline hover:no-underline"
          >
            {t.continueBtn}
          </button>
        </div>
      )}

      {status === 'approved' && (
        <div className="text-center space-y-3">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
          <h3 className="font-bold text-green-700 text-lg">{t.approvedTitle}</h3>
          <p className="text-sm text-gray-600">{t.approvedText}</p>
        </div>
      )}

      {status === 'declined' && (
        <div className="text-center space-y-3">
          <XCircle className="w-16 h-16 text-red-500 mx-auto" />
          <h3 className="font-bold text-red-700 text-lg">{t.declinedTitle}</h3>
          <p className="text-sm text-gray-600">{t.declinedText}</p>
          <a
            href="mailto:support@acquizgroup.com"
            className="inline-block px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition"
          >
            {t.contactSupport}
          </a>
        </div>
      )}

      {status === 'review' && (
        <div className="text-center space-y-3">
          <Clock className="w-16 h-16 text-amber-500 mx-auto" />
          <h3 className="font-bold text-amber-700 text-lg">{t.reviewTitle}</h3>
          <p className="text-sm text-gray-600">{t.reviewText}</p>
        </div>
      )}

      <p className="text-xs text-gray-400 flex items-center gap-1 mt-2">
        <Shield className="w-3 h-3" />
        {t.secure}
      </p>
    </div>
  );
}

export default DiditVerification;
